import { type Brand, type BrandId, type Model, pendingBrandRequestOf } from '@rock/types';

/** Index brands by id for O(1) row rendering. */
export function brandsById(brands: Brand[]): Map<BrandId, Brand> {
    return new Map(brands.map((brand) => [brand.id, brand]));
}

/**
 * The model's LINKED catalog brand, or undefined when it has none (or the catalog is still loading).
 * The one lookup — the decode's brand gate and the brand picker must agree on which doc is authoritative.
 */
export function linkedBrand(model: Model, brands: Brand[]): Brand | undefined {
    return model.brandId ? brands.find((brand) => brand.id === model.brandId) : undefined;
}

/**
 * The model's display brand: the linked catalog brand's name first (`brandId` is an opaque auto-id — it
 * must resolve through the catalog, never render raw), then the enrichment's free-text `details.brand`.
 * `||` so a legacy empty-string brand neither shadows the fallback nor renders as blank.
 */
export function modelBrandLabel(model: Model, byId: Map<BrandId, Brand>): string | undefined {
    if (model.brandId) {
        const brand = byId.get(model.brandId);
        if (brand) return brand.canonicalName;
    }
    return model.details?.brand || undefined;
}

/**
 * How much the model's brand is actually WORTH — the distinction `modelBrandLabel` alone erases. It falls
 * back to the enrichment's free text, so an unlinked guess renders identically to a human-confirmed link.
 * That's exactly the OEM/white-label failure the trust gate exists to catch (a cold-plunge chiller resold
 * under five names), so the list has to show which one it is.
 *
 * - `linked` — `brandId` resolves to a catalog brand. Authoritative.
 * - `suggested` — no link, but a `brandSuggestion` is waiting for a human. Actionable.
 * - `unlinked` — no link and no suggestion, just enrichment free text. Informational only.
 * - `none` — nothing at all.
 */
export type ModelBrandState = 'linked' | 'provisional' | 'suggested' | 'unlinked' | 'none';

export function modelBrandState(model: Model): ModelBrandState {
    // Keyed off `brandId` alone, not off resolving it in the catalog: a set-but-unresolved id just means
    // the brand list is still loading, and flashing "unlinked" on every mount would cry wolf.
    if (model.brandId) return 'linked';
    if (pendingBrandRequestOf(model)) return 'provisional';
    if (model.brandSuggestion) return 'suggested';
    return model.details?.brand ? 'unlinked' : 'none';
}
